import { useCallback, useEffect, useState } from "react";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Loader2, Check, X } from "lucide-react";

const STATUS_META = {
  booked:    { label: "Prenotato",  cls: "bg-[#F5F0FF] text-[#7C3AED] border-[#E9D5FF]" },
  completed: { label: "Completato", cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" },
  cancelled: { label: "Annullato",  cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]" },
};

export default function StaffAgendaPage() {
  const { api, info } = useTenant();
  const tz = info?.timezone || undefined; // orari nel fuso del centro
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("upcoming");
  const [busy, setBusy] = useState(null); // id in lavorazione: niente doppi clic

  const load = useCallback(() => {
    if (!api) return;
    api.allAppointments().then(setItems).catch((e) => setError(fmtError(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    if (busy) return;
    setBusy(id);
    setError("");
    try {
      if (action === "complete") await api.completeAppt(id);
      else if (action === "cancel") await api.cancelAppt(id);
    } catch (e) {
      setError(fmtError(e));
    } finally {
      // si ricarica sempre: se un'altra operatrice l'ha appena annullato lo si vede subito
      await load();
      setBusy(null);
    }
  };

  if (!items) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
  }

  const now = new Date();
  const filtered = items.filter((a) => {
    if (filter === "upcoming") return new Date(a.starts_at) >= now && a.status === "booked";
    if (filter === "completed") return a.status === "completed";
    if (filter === "cancelled") return a.status === "cancelled";
    return true;
  });

  return (
    <div data-testid="agenda-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Agenda</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
            Tutti gli <span className="italic">appuntamenti.</span>
          </h1>
        </div>
        <div className="inline-flex bg-white border border-black/5 rounded-full p-1 text-xs">
          {[
            { k: "upcoming", l: "Prossimi" },
            { k: "completed", l: "Completati" },
            { k: "cancelled", l: "Annullati" },
            { k: "all", l: "Tutti" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`agenda-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-1.5 rounded-full ${filter === f.k ? "bg-[#1A1A1A] text-white" : "text-[#737373]"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="mb-6 text-sm text-[#B91C1C]">{error}</div>}

      <div className="rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="agenda-list">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[#737373] font-light">Nessun appuntamento in questa vista.</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {filtered.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.booked;
              return (
                <li key={a.id} data-testid={`agenda-${a.id}`} className="px-6 md:px-8 py-5 flex items-center gap-4 flex-wrap">
                  <div className="text-center min-w-[64px]">
                    <div className="font-serif text-2xl text-[#1A1A1A]">
                      {new Date(a.starts_at).toLocaleDateString("it-IT", { day: "2-digit", timeZone: tz })}
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#737373]">
                      {new Date(a.starts_at).toLocaleDateString("it-IT", { month: "short", timeZone: tz })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-[#1A1A1A]">{a.service_name}</div>
                    {/* appuntamento preso in salone per una cliente senza account online (P2b) */}
                    <div className="text-xs text-[#737373]">{a.client_name || (a.source === "desktop" ? "Cliente in salone" : "—")} · {fmtDate(a.starts_at, tz)} · {a.duration_minutes} min</div>
                    {a.notes && <div className="mt-1 text-xs text-[#737373] italic">"{a.notes}"</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-[#1A1A1A]">{fmtEUR(a.price)}</div>
                    <span className={`mt-1 inline-block text-[10px] tracking-[0.2em] uppercase border rounded-full px-2 py-0.5 ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </div>
                  {a.status === "booked" && (
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`complete-${a.id}`}
                        onClick={() => act(a.id, "complete")}
                        disabled={!!busy}
                        className="disabled:opacity-50 inline-flex items-center gap-1 text-xs bg-[#1A1A1A] text-white rounded-full px-3 py-1.5 hover:bg-[#333]"
                      >
                        <Check size={12} /> Completa
                      </button>
                      <button
                        data-testid={`cancel-${a.id}`}
                        onClick={() => act(a.id, "cancel")}
                        disabled={!!busy}
                        className="disabled:opacity-50 inline-flex items-center gap-1 text-xs bg-white border border-black/10 text-[#1A1A1A] rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]"
                      >
                        <X size={12} /> Annulla
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
